import { createStore } from 'zustand/vanilla';

export type Theme = 'light' | 'dark';
export type ThemePreference = Theme | 'system';
export const THEME_STORAGE_KEY = 'tokentax-dark-mode';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

// Keep the boolean storage format used by existing clients and browser tabs.
function readPreference(browser: Window, fallback: ThemePreference = 'system'): ThemePreference {
  try {
    const value = browser.localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'true' ? 'dark' : value === 'false' ? 'light' : 'system';
  } catch {
    return fallback;
  }
}

export function createThemeController(browser: Window) {
  const media = browser.matchMedia?.(MEDIA_QUERY);
  const resolveTheme = (preference: ThemePreference): Theme =>
    preference === 'system' ? (media?.matches ? 'dark' : 'light') : preference;
  const preference = readPreference(browser);
  const store = createStore(() => ({ preference, theme: resolveTheme(preference) }));

  const update = (next: ThemePreference) => {
    store.setState({ preference: next, theme: resolveTheme(next) });
  };

  const setPreference = (next: ThemePreference) => {
    try {
      if (next === 'system') browser.localStorage.removeItem(THEME_STORAGE_KEY);
      else browser.localStorage.setItem(THEME_STORAGE_KEY, String(next === 'dark'));
    } catch {
      // A blocked or full storage area must not prevent switching this session.
    }
    update(next);
  };
  const toggle = () => setPreference(store.getState().theme === 'dark' ? 'light' : 'dark');
  const enable = () => setPreference('dark');
  const disable = () => setPreference('light');

  // One owner at the app root; consumers only subscribe to the store.
  const attach = () => {
    const applyTheme = () => {
      const { theme } = store.getState();
      const root = browser.document.documentElement;
      root.classList.toggle('dark', theme === 'dark');
      root.classList.toggle('light', theme === 'light');
      root.style.colorScheme = theme;
    };
    const onSystemChange = () => {
      if (store.getState().preference === 'system') update('system');
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
      try {
        if (event.storageArea && event.storageArea !== browser.localStorage) return;
      } catch {
        return;
      }
      update(readPreference(browser));
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      const editing =
        target instanceof Element &&
        target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])');
      if (
        event.code === 'KeyT' &&
        event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.repeat &&
        !event.isComposing &&
        !event.defaultPrevented &&
        !editing
      ) {
        event.preventDefault();
        toggle();
      }
    };
    const unsubscribe = store.subscribe(applyTheme);
    // Re-read on mount to include changes made while the application was unmounted.
    update(readPreference(browser, store.getState().preference));
    if (media?.addEventListener) media.addEventListener('change', onSystemChange);
    else media?.addListener?.(onSystemChange);
    browser.addEventListener('storage', onStorage);
    browser.addEventListener('keydown', onKeyDown);
    return () => {
      unsubscribe();
      if (media?.removeEventListener) media.removeEventListener('change', onSystemChange);
      else media?.removeListener?.(onSystemChange);
      browser.removeEventListener('storage', onStorage);
      browser.removeEventListener('keydown', onKeyDown);
    };
  };

  let owners = 0;
  let detach: (() => void) | undefined;
  const start = () => {
    if (owners++ === 0) detach = attach();
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      if (--owners === 0) detach?.();
    };
  };

  return { store, start, setPreference, toggle, enable, disable };
}

export const themeController = createThemeController(window);
